import React from 'react';
import {
  Box,
  Modal,
  Paper,
  Typography,
  Stack,
  Chip,
  LinearProgress,
  Fade,
  Backdrop
} from '@mui/material';
import { TrioSelectionState } from '../types/voiceOrderingTypes';

interface TrioSelectionModalProps {
  trioSelection: TrioSelectionState | null;
  isOpen: boolean;
  currentTranscript: string;
}

export default function TrioSelectionModal({
  trioSelection,
  isOpen,
  currentTranscript
}: TrioSelectionModalProps) {
  
  if (!trioSelection) return null;

  const { trioDetails, currentGroupIndex, selections, isLoading } = trioSelection;
  const currentGroup = trioDetails.option_groups[currentGroupIndex];
  const totalGroups = trioDetails.option_groups.length;
  const progress = totalGroups > 0 ? ((currentGroupIndex + 1) / totalGroups) * 100 : 0;

  return (
    <Modal
      open={isOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Fade in={isOpen}>
        <Paper
          elevation={24}
          sx={{
            width: '90%',
            maxWidth: 800,
            maxHeight: '90vh',
            overflow: 'auto',
            borderRadius: 4,
            bgcolor: 'white',
            position: 'relative'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: '#D32F2F',
              color: 'white',
              p: 3,
              borderRadius: '16px 16px 0 0'
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h4" fontWeight="bold" textAlign="center">
                🍽️ Configuration Trio
              </Typography>
              
              <Typography variant="h5" textAlign="center">
                {trioDetails.product.name}
              </Typography>
              
              <Typography variant="body1" textAlign="center" sx={{ opacity: 0.9 }}>
                Prix trio: ${trioDetails.product.trioPrice ? trioDetails.product.trioPrice.toFixed(2) : '0.00'}
              </Typography>

              {/* Progress Bar */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">
                    Étape {currentGroupIndex + 1} sur {totalGroups}
                  </Typography>
                  <Typography variant="body2">
                    {Math.round(progress)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'white',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>
            </Stack>
          </Box>

          {/* Current Selection Section */}
          <Box sx={{ p: 3 }}>
            {totalGroups > 0 ? (
              currentGroup && (
                <Stack spacing={3}>
                  {/* Current Group */}
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                      🎯 {currentGroup.name}
                      {currentGroup.required && (
                        <Chip 
                          label="Obligatoire" 
                          size="small" 
                          color="error" 
                          sx={{ ml: 1 }} 
                        />
                      )}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {currentGroup.multiple 
                        ? "Vous pouvez choisir plusieurs options" 
                        : "Choisissez une seule option"
                      }
                    </Typography>

                    {/* Available Options */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Options disponibles:
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {currentGroup.items.map((item) => (
                          <Chip
                            key={item.id}
                            label={`${item.name} (+$${item.price.toFixed(2)})`}
                            variant="outlined"
                            sx={{
                              borderColor: '#D32F2F',
                              color: '#D32F2F',
                              '&:hover': {
                                bgcolor: 'rgba(211, 47, 47, 0.1)'
                              }
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Box>

                  {/* Voice Input Display */}
                  <Box
                    sx={{
                      bgcolor: '#f5f5f5',
                      borderRadius: 2,
                      p: 2,
                      border: '2px dashed #D32F2F'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body2" fontWeight="bold" color="#D32F2F">
                        🎤 Votre choix:
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          flex: 1,
                          minHeight: 24,
                          fontStyle: currentTranscript ? 'normal' : 'italic',
                          color: currentTranscript ? 'text.primary' : 'text.secondary'
                        }}
                      >
                        {currentTranscript || "Parlez maintenant..."}
                      </Typography>
                      {isLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Traitement...
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Previous Selections */}
                  {Object.keys(selections).length > 0 && (
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                        ✅ Sélections confirmées
                      </Typography>
                      <Stack spacing={1}>
                        {trioDetails.option_groups.map((group) => {
                          const groupSelections = selections[group.id];
                          if (!groupSelections || groupSelections.length === 0) return null;

                          return (
                            <Box key={group.id} sx={{ pl: 2 }}>
                              <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                {group.name}:
                              </Typography>
                              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                                {groupSelections.map((selection) => (
                                  <Chip
                                    key={selection.item_id}
                                    label={`${selection.name} (+$${selection.price.toFixed(2)})`}
                                    size="small"
                                    color="success"
                                    variant="filled"
                                  />
                                ))}
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Instructions */}
                  <Box
                    sx={{
                      bgcolor: '#e3f2fd',
                      borderRadius: 2,
                      p: 2,
                      border: '1px solid #2196f3'
                    }}
                  >
                    <Typography variant="body2" color="#1976d2" textAlign="center">
                      💡 <strong>Instructions:</strong> Dites clairement le nom de votre choix. Dites {"\"suivant\""} pour passer au groupe suivant, {"\"précédent\""} pour revenir, {"\"annuler trio\""} pour annuler, {"\"répéter\""} pour réécouter les options.
                      {currentGroupIndex === totalGroups - 1 
                        ? " Une fois terminé, dites \"valider ma demande trio\"."
                        : " Nous passerons automatiquement à la prochaine sélection."
                      }
                    </Typography>
                  </Box>
                </Stack>
              )
            ) : (
              <Stack spacing={3} alignItems="center">
                <Box
                  sx={{
                    bgcolor: '#fff9c4',
                    borderRadius: 2,
                    p: 4,
                    border: '2px solid #fbc02d',
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  <Typography variant="h6" color="#f57f17" gutterBottom>
                    ⚠️ Aucune option de configuration
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Ce produit est configuré comme un trio, mais aucune option n&apos;a été ajoutée.
                  </Typography>
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      💡 Action vocale requise:
                    </Typography>
                    <Typography variant="body2">
                      Dites <strong>&quot;valider ma demande trio&quot;</strong> pour l&apos;ajouter tel quel, ou <strong>&quot;annuler trio&quot;</strong> pour revenir en arrière.
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              bgcolor: '#f5f5f5',
              p: 2,
              borderRadius: '0 0 16px 16px',
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              🎙️ Interface contrôlée entièrement par la voix
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}
